import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useLocation } from 'react-router-dom'
import IntelLayout from './components/IntelLayout'
import Badge from './components/Badge'
import { intelApi } from './api'
import { intelColors, formatNumber } from './components/intelTheme'

const dirLabel = (d) => (d === 'RVP' ? 'Reverse' : d === 'FORWARD' ? 'Forward' : d || '—')

const SECTIONS = [
  { id: 'fwd', label: 'Forward', icon: '🔵', categories: ['FORWARD'], color: 'blue', tone: '#e3f2fd' },
  { id: 'rev', label: 'Reverse', icon: '🟠', categories: ['REVERSE'], color: 'orange', tone: '#fff3e0' },
  { id: 'hv', label: 'High Value', icon: '💎', categories: ['HIGH_VALUE'], color: 'purple', tone: '#f3e5f5' },
  { id: 'exp', label: 'SDD / NDD', icon: '⚡', categories: ['SDD_NDD'], color: 'green', tone: '#e8f5e9' },
  { id: 'hl', label: 'Hyperlocal', icon: '🏙️', categories: ['HYPERLOCAL'], color: 'grey', tone: '#f5f5f5' },
]

export default function ServiceTypeGuide() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const refs = useRef({})
  const location = useLocation()

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .serviceTypes({ includeLinkedCodes: true })
      .then((res) => !cancel && setData(res?.data?.responseObj || res?.data?.data || res?.data))
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [])

  useEffect(() => {
    const hash = location.hash?.replace('#', '')
    if (hash && refs.current[hash]) {
      setTimeout(() => refs.current[hash]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200)
    }
  }, [location.hash, data])

  const sts = data?.serviceTypes || []

  const grouped = useMemo(() => {
    const map = {}
    SECTIONS.forEach((s) => (map[s.id] = []))
    sts.forEach((st) => {
      const sec = SECTIONS.find((s) => s.categories.includes(st.category))
      if (sec) map[sec.id].push(st)
    })
    return map
  }, [sts])

  return (
    <IntelLayout title='Service Type Definitions'>
      <Box sx={{ background: '#f0f7ff', border: '1px solid #bbdefb', borderRadius: '8px', p: 2, mb: 2 }}>
        <Typography sx={{ fontSize: 12, color: '#1565c0' }}>
          Definitions of each Service Type — what it means, who it serves, SLA, and the courier codes linked to it.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {SECTIONS.map((s) => (
          <Box
            key={s.id}
            onClick={() => refs.current[s.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            sx={{
              cursor: 'pointer',
              padding: '6px 14px',
              borderRadius: '20px',
              border: `2px solid ${intelColors.primary}`,
              color: intelColors.primary,
              fontSize: 12,
              fontWeight: 600,
              background: '#fff',
              '&:hover': { background: '#e3f2fd' },
            }}
          >
            {s.icon} {s.label}
          </Box>
        ))}
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      {loading && <Typography sx={{ fontSize: 12, color: intelColors.muted }}>Loading…</Typography>}

      {SECTIONS.map((s) => (
        <Box key={s.id} ref={(el) => (refs.current[s.id] = el)} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: intelColors.text }}>{s.icon} {s.label}</Typography>
            <Badge color={s.color}>{grouped[s.id]?.length || 0} types</Badge>
            <Box sx={{ flex: 1, height: 1, background: intelColors.border }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 2 }}>
            {grouped[s.id]?.map((st) => (
              <Box key={st.code} sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', p: 2, borderTop: `4px solid ${intelColors.primary}`, position: 'relative', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <code style={{ background: '#f1f3f5', padding: '2px 6px', borderRadius: 3, fontSize: 11.5, fontWeight: 700, color: '#1565c0' }}>{st.code}</code>
                  {st.active ? <Badge color='green'>Active</Badge> : <Badge color='grey'>Inactive</Badge>}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text, mb: 0.5 }}>{st.name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: intelColors.subtext, mb: 1, lineHeight: 1.5 }}>{st.description || '—'}</Typography>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  <Badge color={st.direction === 'RVP' ? 'orange' : 'blue'}>{dirLabel(st.direction)}</Badge>
                  {st.mode && <Badge color={st.mode === 'AIR' ? 'purple' : st.mode === 'HYPERLOCAL' ? 'grey' : 'blue'}>{st.mode}</Badge>}
                  {st.weightBand && <Badge color='grey'>{st.weightBand}</Badge>}
                  {st.requiresQc && <Badge color='blue'>QC</Badge>}
                  {st.requiresInsurance && <Badge color='purple'>Insurance</Badge>}
                  {st.requiresOtp && <Badge color='blue'>OTP</Badge>}
                </Box>

                <Box sx={{ fontSize: 11, color: intelColors.subtext, mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <span><strong>SLA:</strong> {st.slaDescription || '—'}</span>
                  <span><strong>Weight:</strong> {st.weightRangeLabel || '—'}</span>
                </Box>

                {st.baseServiceType && (
                  <Typography sx={{ fontSize: 11, color: intelColors.purple, mb: 1 }}>↳ Routes via <code>{st.baseServiceType}</code></Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1.5, mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                  <Box>
                    <Typography sx={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Codes</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatNumber(st.totalLinkedCount)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Pickup</Typography>
                    <Typography sx={{ fontSize: 13 }}>{formatNumber(st.pickupPinCount)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Delivery</Typography>
                    <Typography sx={{ fontSize: 13 }}>{formatNumber(st.deliveryPinCount)}</Typography>
                  </Box>
                </Box>

                {st.exampleAccountCodes?.length > 0 && (
                  <Box sx={{ mt: 1, fontSize: 10.5, color: intelColors.muted }}>
                    <strong>Examples: </strong>
                    {st.exampleAccountCodes.slice(0, 5).map((c, i) => (
                      <code key={i} style={{ background: '#f8f9fb', padding: '1px 4px', borderRadius: 2, marginRight: 4 }}>{c}</code>
                    ))}
                  </Box>
                )}

              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </IntelLayout>
  )
}
